import { useState, useEffect, useRef } from 'react';

export interface OnCarouselIndexChangeAttemptProps {
  newIndex: number;
  prevIndex: number;
  didChange: boolean;
  direction: 'next' | 'prev';
  isFirstAttempt: boolean;
}

function useCarousel(
  slideCount: number,
  onCarouselIndexChangeAttempt: (
    args: OnCarouselIndexChangeAttemptProps
  ) => void,
  controlledIndex?: number,
  stepSize: number = 1
): {
  currentIndex: number;
  gotoSlide: (index: number) => void;
  gotoNextSlide: () => void;
  gotoPrevSlide: () => void;
} {
  const isDirtyRef = useRef(false);
  const [currentIndex, setCurrentIndex] = useState<number>(
    controlledIndex || 0
  );

  // Keep internal state synced with controlled index
  useEffect(() => {
    if (
      typeof controlledIndex === 'number' &&
      controlledIndex >= 0 &&
      controlledIndex < slideCount &&
      controlledIndex !== currentIndex
    ) {
      setCurrentIndex(controlledIndex);
    }
  }, [controlledIndex, slideCount, currentIndex]);

  const gotoSlide = (index: number): void => {
    setCurrentIndex(index);
  };

  const gotoNextSlide = (): void => {
    setCurrentIndex((prevIndex: number) => {
      const newIndex = (prevIndex + stepSize) % slideCount;
      onCarouselIndexChangeAttempt({
        newIndex,
        prevIndex,
        didChange: newIndex !== prevIndex,
        direction: 'next',
        isFirstAttempt: !isDirtyRef.current,
      });
      isDirtyRef.current = true;
      return newIndex;
    });
  };

  const gotoPrevSlide = (): void => {
    setCurrentIndex((prevIndex: number) => {
      const newIndex = (prevIndex - stepSize + slideCount) % slideCount;
      onCarouselIndexChangeAttempt({
        newIndex,
        prevIndex,
        didChange: newIndex !== prevIndex,
        direction: 'prev',
        isFirstAttempt: !isDirtyRef.current,
      });
      isDirtyRef.current = true;
      return newIndex;
    });
  };

  return { currentIndex, gotoSlide, gotoNextSlide, gotoPrevSlide };
}

export default useCarousel;
